<template>
  <div class="h-full flex flex-col">
    <!-- Header - Only Mobile -->
    <div v-if="isMobile" class="px-4 py-3 border-b">
      <div class="flex items-center justify-between">
        <h3 class="text-base font-semibold leading-6 text-gray-900">
          Filtreler
        </h3>
        <UButton
          color="gray"
          variant="ghost"
          icon="i-heroicons-x-mark-20-solid"
          class="-my-1"
          @click="$emit('close')"
        />
      </div>
    </div>

    <!-- Scrollable Content -->
    <div class="flex-1 overflow-y-auto">
      <div :class="[isMobile ? 'p-4' : '']">
        <div :class="{ 'pointer-events-none opacity-50 animate-pulse': isHydrated && isLoading }">
          <UCard
            v-if="!isMobile"
            class="sticky top-4"
          >
            <template #header>
              <div class="flex items-center justify-between">
                <h3 class="text-lg font-semibold tracking-tight">Filtreler</h3>
              </div>
            </template>

            <div>
              <!-- Category Filter -->
              <div 
                v-if="filters?.categories?.length" 
                class="py-4"
                :class="{ 'border-b border-gray-100': hasBrands || hasAttributes || hasPriceRange }"
              >
                <FrontendCatalogFiltersCategories
                  :categories="filters?.categories"
                  :category-selection-mode="categorySelectionMode"
                  :is-category-selected="isCategorySelected"
                  class="cursor-pointer"
                  @select-category="handleCategorySelect"
                />
              </div>

              <!-- Brand Filter -->
              <div 
                v-if="hasBrands" 
                class="py-4"
                :class="{ 'border-b border-gray-100': hasAttributes || hasPriceRange }"
              >
                <FrontendCatalogFiltersBrands
                  :brands="filters?.brands"
                  :selected-brands="activeFilters.brands"
                  class="cursor-pointer"
                  @toggle="toggleBrand"
                />
              </div>

              <!-- Attribute Filters -->
              <div 
                v-if="hasAttributes" 
                class="py-4"
                :class="{ 'border-b border-gray-100': hasPriceRange }"
              >
                <FrontendCatalogFiltersAttributes
                  :attributes="filters?.attributes"
                  :selected-attributes="activeFilters.attributes"
                  class="cursor-pointer"
                  @toggle="toggleAttribute"
                />
              </div>

              <!-- Price Filter -->
              <div 
                v-if="hasPriceRange" 
                class="py-4"
              >
                <FrontendCatalogFiltersPrice
                  :price-range="filters?.price_range"
                  :selected-min="activeFilters.price.min"
                  :selected-max="activeFilters.price.max"
                  class="cursor-pointer"
                  @update="updatePriceRange"
                />
              </div>
            </div>
          </UCard>

          <!-- Mobile View Content -->
          <div v-else class="space-y-4">
            <!-- Category Filter -->
            <FrontendCatalogFiltersCategories 
              v-if="filters?.categories?.length"
              :categories="filters?.categories"
              :category-selection-mode="categorySelectionMode"
              :is-category-selected="isCategorySelected"
              @select-category="handleCategorySelect"
            />

            <!-- Brand Filter -->
            <FrontendCatalogFiltersBrands
              v-if="hasBrands"
              :brands="filters?.brands"
              :selected-brands="activeFilters.brands"
              @toggle="toggleBrand"
            />

            <!-- Attribute Filters -->
            <FrontendCatalogFiltersAttributes
              v-if="hasAttributes"
              :attributes="filters?.attributes"
              :selected-attributes="activeFilters.attributes"
              @toggle="toggleAttribute"
            />

            <!-- Price Filter -->
            <FrontendCatalogFiltersPrice
              v-if="hasPriceRange"
              :price-range="filters?.price_range"
              :selected-min="activeFilters.price.min"
              :selected-max="activeFilters.price.max"
              @update="updatePriceRange"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- Fixed Footer - Mobile Only -->
    <div v-if="isMobile" class="border-t bg-white">
      <div class="p-4">
        <UButton
          color="orange"
          block
          :loading="isLoading"
          :disabled="isLoading"
          @click="$emit('close')"
        >
          Sonuçları Gör
        </UButton>
      </div>
    </div>
  </div>
</template>

<script setup>
const { isMobile } = useDevice()
const isHydrated = ref(false)
onMounted(() => {
  isHydrated.value = true
})

const props = defineProps({
  filters: {
    type: Object,
    required: true,
    default: () => ({
      brands: [],
      price_range: null,
      attributes: []
    })
  },
  activeFilters: {
    type: Object,
    required: true,
    default: () => ({
      brands: [],
      price: {
        min: null,
        max: null
      },
      attributes: {},
      selected_categories: []
    })
  },
  isLoading: {
    type: Boolean,
    default: false
  },
  categorySelectionMode: {
    type: String,
    default: 'link',
    validator: (value) => ['link', 'checkbox'].includes(value)
  },
  isCategorySelected: {
    type: Function,
    default: () => false
  }
})

const emit = defineEmits(['update:filters', 'close', 'select-category'])

// Mobil için geçici filtre state'i
const tempFilters = ref({ ...props.activeFilters })

// Computed for checking if any filters are active
const hasActiveFilters = computed(() => {
  return (
    props.activeFilters?.brands?.length > 0 ||
    props.activeFilters?.price?.min !== null ||
    props.activeFilters?.price?.max !== null ||
    Object.keys(props.activeFilters?.attributes || {}).length > 0
  )
})

// Filtre varlığını kontrol eden computed değerler
const hasBrands = computed(() => props.filters?.brands?.length > 0)
const hasAttributes = computed(() => props.filters?.attributes?.length > 0)
const hasPriceRange = computed(() => !!props.filters?.price_range)

// Methods
const toggleBrand = (brandId) => {
  const filters = { ...props.activeFilters }
  const brands = [...(filters.brands || [])]
  const index = brands.indexOf(brandId)

  if (index === -1) {
    brands.push(brandId)
  } else {
    brands.splice(index, 1)
  }

  emit('update:filters', { ...filters, brands })
}

const toggleAttribute = (attributeId, termId) => {
  const filters = { ...props.activeFilters }
  const currentAttributes = { ...(filters.attributes || {}) }

  if (!currentAttributes[attributeId]) {
    currentAttributes[attributeId] = [termId]
  } else {
    const index = currentAttributes[attributeId].indexOf(termId)
    if (index === -1) {
      currentAttributes[attributeId].push(termId)
    } else {
      currentAttributes[attributeId].splice(index, 1)
      if (currentAttributes[attributeId].length === 0) {
        delete currentAttributes[attributeId]
      }
    }
  }

  emit('update:filters', { ...filters, attributes: currentAttributes })
}

const updatePriceRange = ({ min, max }) => {
  const filters = { ...props.activeFilters }
  emit('update:filters', { ...filters, price: { min, max } })
}

const resetFilters = () => {
  emit('update:filters', {
    brands: [],
    price: {
      min: null,
      max: null
    },
    attributes: {}
  })
}

const handleCategorySelect = (categoryId) => {
  emit('select-category', categoryId)
}

// Watch props changes to update temp filters
watch(
  () => props.activeFilters,
  (newFilters) => {
    if (isMobile) {
      tempFilters.value = { ...newFilters }
    }
  },
  { deep: true }
)
</script>

<style scoped>
.overflow-y-auto {
  -webkit-overflow-scrolling: touch;
  scrollbar-width: thin;
  scrollbar-color: #cbd5e1 transparent;
}

.overflow-y-auto::-webkit-scrollbar {
  width: 6px;
}

.overflow-y-auto::-webkit-scrollbar-track {
  background: transparent;
}

.overflow-y-auto::-webkit-scrollbar-thumb {
  background-color: #cbd5e1;
  border-radius: 3px;
}
</style>
